import { flow, getRoot, Instance, SnapshotOut, types, resolveIdentifier } from "mobx-state-tree"
import { Platform } from "~/ui/primitives"
import RNHeicConverter from "~/ui/heic-converter"
import { ZONE, upload as qiniuUpload } from "~/ui/qiniu"
import { updateMessageByMessageID } from "~services/sqlite"
import { Attach, MessageStatus, MessageTypes, ProductOrder, AttachTransData, AttachRedPacketData } from "../../proto"
import { numEnumeration } from "../../types/enum"
import { debug, staticUrl } from "../../utils"
import { withEnvironment } from "../extensions/with-environment"
import { UserModel } from "../user"
import { RootStoreModel } from ".."

const covertHEICtoJPG = (uri) => {
  return new Promise((resolve) => {
    RNHeicConverter.convert({
      path: uri,
    }).then((result) => {
      if (result.success) {
        resolve(result.path)
      } else {
        resolve(undefined)
      }
    })
  })
}

export const MessageModel = types
  .model("Message")
  .props({
    message_id: types.identifier,
    id: types.maybeNull(types.number), // 服务db的消息primary_key
    sid: types.maybeNull(types.number), // 本地db的消息primary_key
    chat_id: types.maybeNull(types.number), // 服务db的聊天室primary_key
    chat_cid: types.maybeNull(types.string), // 本地mobx的聊天室primary_key
    sender: types.safeReference(UserModel),
    receiver: types.maybeNull(types.safeReference(UserModel)),
    content: types.optional(types.string, ""),
    created: types.Date,
    updated: types.Date,
    attach: types.maybeNull(types.frozen<Attach & { localVideoPreviewImageUri?: string }>()), // 本地附件
    attach_path: types.maybeNull(types.string), // 本地附件保存路径
    type: types.optional(
      numEnumeration<MessageTypes>("MessageType", Object.values(MessageTypes)),
      MessageTypes.TEXT,
    ),
    status: types.optional(
      numEnumeration<MessageStatus>("MessageStatus", Object.values(MessageStatus)),
      MessageStatus.SENDING,
    ),
    read_ids: types.optional(types.array(types.number), []),
    notify_type: types.maybeNull(types.number),
    mention_ids: types.optional(types.array(types.number), []),
    reply_msg_id: types.maybeNull(types.string),
    forward_msg_id: types.maybeNull(types.string),
    forward_user_id: types.maybeNull(types.number),
    forward_name: types.maybeNull(types.string),
    burning_timer: types.maybeNull(types.number),
    is_deleted: types.maybeNull(types.boolean),
  })
  .volatile(() => ({
    progress: 0,
  }))
  .extend(withEnvironment)
  .views((self) => {
    function expireTime() {
      return self.created.valueOf() + 15 * 60 * 1000
    }
    function messageChatID() {
      return self.chat_cid
    }
    function replyMsg() {
      const root = getRoot<Instance<typeof RootStoreModel>>(self)
      return self.reply_msg_id ? resolveIdentifier(MessageModel, root, self.reply_msg_id) : null
    }
    function attachURL() {
      return staticUrl(self.content)
    }
    // 获取附件地址
    function getAttachFile() {
      return self.attach_path || staticUrl(self.content)
    }

    // 消息附件是否已经成功下载
    function hasAttachFile() {
      return hasAttach() && self.attach_path !== null
    }

    // 消息是否包含附件
    function hasAttach() {
      return !!self.attach || false
    }
    // 图片文件
    function image() {
      return getAttachFile()
    }
    // 音频文件
    function voice() {
      return getAttachFile()
    }
    function loading() {
      return !!self.id || false
    }
    function uploading() {
      return self.status === MessageStatus.UPLOADING
    }
    function inProgress() {
      return self.status === MessageStatus.UPLOADING || self.status === MessageStatus.DOWNLOADING
    }
    // 本人发送
    function byMe() {
      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      return currentUser.id === self.sender?.id
    }
    function isReadedByOther() {
      return self.read_ids ? self.read_ids.length > 0 : false
    }
    function isReadedByMe() {
      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      return self.read_ids ? self.read_ids.includes(currentUser.id) : false
    }
    function isNotify() {
      return !!self.notify_type || false
    }
    return {
      get expireTime() {
        return expireTime()
      },
      get messageChatID() {
        return messageChatID()
      },
      get replyMsg() {
        return replyMsg()
      },
      get attachURL() {
        return attachURL()
      },
      get getAttachFile() {
        return getAttachFile()
      },
      get hasAttachFile() {
        return hasAttachFile()
      },
      get hasAttach() {
        return hasAttach()
      },
      get image() {
        return image()
      },
      get voice() {
        return voice()
      },
      get loading() {
        return loading()
      },
      get uploading() {
        return uploading()
      },
      get inProgress() {
        return inProgress()
      },
      get byMe() {
        return byMe()
      },
      get isReadedByOther() {
        return isReadedByOther()
      },
      get isReadedByMe() {
        return isReadedByMe()
      },
      get isNotify() {
        return isNotify()
      },
    }
  })
  .actions((self) => ({
    setOrderEdited(v: boolean) {
      self.attach = {
        ...self.attach,
        order: {
          ...self.attach.order,
          edited: v,
        },
      }
    },
    update(values: { [key: string]: any }) {
      for (const key in values) {
        self[key] = values[key]
      }
    },
    setStatus(status: MessageStatus) {
      self.status = status
    },
    markRead() {
      const {
        userStore: { currentUser },
      } = getRoot<Instance<typeof RootStoreModel>>(self)
      self.status = MessageStatus.READ
      if (!self.read_ids) self.read_ids.replace([currentUser.id])
      else if (!self.read_ids.includes(currentUser.id)) self.read_ids.push(currentUser.id)
      updateMessageByMessageID({ cM: self })
    },
    // 设置上传/下载进度
    setProgress(progress: number) {
      debug(self.getAttachFile, progress)
      self.progress = progress
    },
    setAttachPath(path: string) {
      self.attach_path = path
    },
    setLocalVideoPreviewImageUri(uri: string) {
      self.attach = {
        ...self.attach,
        localVideoPreviewImageUri: uri,
      }
    },
    setTransInfo(data: Partial<AttachTransData>) {
      self.attach = {
        ...self.attach,
        trans_info: {
          ...self.attach?.trans_info,
          ...data,
        },
      }
      updateMessageByMessageID({ cM: self })
    },
    setRedPacketInfo(data: Partial<AttachRedPacketData>) {
      self.attach = {
        ...self.attach,
        redpacket_info: {
          ...self.attach?.redpacket_info,
          ...data,
        },
      }
      updateMessageByMessageID({ cM: self })
    }
  }))
  .actions((self) => {
    // 下载附件
    const download = flow(function* ({ onProgressUpdate }) {
      const { apiStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const payload = {
        url: staticUrl(self.content),
        onProgressUpdate: onProgressUpdate,
      }
      const prevStatus = self.status
      try {
        self.status = MessageStatus.DOWNLOADING
        const path = yield apiStore.download(payload)
        self.attach_path = Platform.OS === "ios" ? "file://" + path : path
        self.status = prevStatus
        return self.attach_path
      } catch (e) {
        self.status = MessageStatus.FAILED
        throw new Error(e.message)
      }
    })

    // 上传附件
    const upload = flow(function* () {
      const { apiStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const payload = {
        ...self.attach,
        path: self.attach_path,
        onProgressUpdate: self.setProgress,
      }
      const st = self.status
      try {
        
        self.status = MessageStatus.UPLOADING
        const data = yield apiStore.upload(payload)
        debug("upload response:", data)
        self.content = data.uri
        if (Platform.OS === "web" && data.uri) {
          self.attach_path = staticUrl(data.uri)
        }
        self.status = st
        return data
      } catch (e) {
        self.status = MessageStatus.FAILED
        throw new Error(e.message)
      }
    })

    const uploadClient = flow(function* () {
      const { apiStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const payload: any = {
        ...self.attach,
        path: self.attach_path,
      }
      const st = self.status
      try {
        self.status = MessageStatus.UPLOADING
        const data = yield apiStore.uploadClient(payload)
        debug("uploadClient response:", data)
        if (data?.storage === "S3Storage") {
          payload.onProgressUpdate = ({ loaded, total }) => {
            self.setProgress(loaded / total)
          }
          const newFormData = new FormData()
          Object.keys(data.payload.fields).forEach((key) => {
            newFormData.append(key, data.payload.fields[key])
          })
          newFormData.append("file", {
            uri: payload.path,
            name: payload.filename,
            type: payload.type,
          } as any)
          yield apiStore.uploadToS3Storage(data.payload.url, newFormData, payload.onProgressUpdate)
        } else if (data?.storage === "QiNiuStorage") {
          payload.onProgressUpdate = (progress: number) => {
            self.setProgress(progress)
          }
          if (!data.payload.url) {
            data.payload.url = "https://prenbqn.bj-huishou.com"
          }
          const uploadData = {
            // 上传到云端的文件名
            key: data.url,
            // 上传文件的本地路径
            path: payload.path.startsWith("file://")
              ? payload.path.split("file://")[1]
              : payload.path,
            // 上传文件的 mime type
            mimeType: payload.type,
            // 上传凭证，通常由服务器生成传给客户端
            token: data.payload.token,
            // 机房，传入 ZONE 的枚举值
            zone: ZONE.HUANAN,
          }
          yield qiniuUpload(uploadData, payload.onProgressUpdate)
        }
        self.content = data.payload.url + "/" + data.url
        self.attach_path = self.content
        debug("uploaded video url:", self.content)
        self.status = st
        return data
      } catch (e) {
        self.status = MessageStatus.FAILED
        throw new Error(e.message)
      }
    })

    const uploadMultipleImages = flow(function* () {
      const { apiStore } = getRoot<Instance<typeof RootStoreModel>>(self)
      const st = self.status
      try {
        self.status = MessageStatus.UPLOADING
        const setProgress = self.setProgress
        const results = yield Promise.all(
          self.attach.imgs.map(async (img: any) => {
            // console.log("--img---", img)  
            img.progress = 0
            if (/HEIC$/i.test(img.type)) {
              const uri = await covertHEICtoJPG(img.uri)
              if (uri) {
                img.uri = uri
                img.type = "photo/jpg"
                img.filename = img.filename.replace(/\.HEIC$/i, ".jpeg")
              }
            }
            const payload = {
              path: img.uri,
              type: img.type,
              filename: img.filename,
              size: img.size,
              onProgressUpdate: (progress) => {
                setProgress(Math.random())
                img.progress = progress
              },
            }
            img.isUploading = true
            
            const data = await apiStore.upload(payload)
            img.isUploading = false
            img.url = data.uri
            return data
          }),
        )
        self.status = st
        return results
      } catch (e) {
        self.status = MessageStatus.FAILED
        throw new Error(e.message)
      }
    })
    return { upload, uploadMultipleImages, download, uploadClient }
  })

type MessageType = Instance<typeof MessageModel>
export type Message = MessageType & {
  banner?: { bannerId: string; imageUrl: string; title: string }
  notify_type: number
  receiver: {
    id: number;
    name: string;
    username: string;
    phone: string;
    avatar?: string;
    email?: string;
    gender?: number;
    online?: boolean;
    is_id_verify?: boolean;
    is_pay_password?: boolean;
    is_phone_verify?: boolean;
  }
  mention_ids: string
  type: number
  byMe: boolean
  content?: string
  order?: ProductOrder
  isNotify?: boolean
}
type MessageSnapshotType = SnapshotOut<typeof MessageModel>
export interface MessageSnapshot extends MessageSnapshotType {}
export const createMessageDefaultModel = () => types.optional(MessageModel, {})

export const lateMessage = () => {
  return MessageModel
}
